import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'unight_promo_dismissed';

/**
 * Site-wide promotional pop-up. Renders when:
 *   - an active promotion exists on the server
 *   - the current browser hasn't dismissed THIS version (version bumps on
 *     any content edit so a re-saved promo surfaces again).
 *
 * One component mounted at app root → shows across every page, both mobile
 * and desktop. Button URL supports internal (/events/xyz) and external links.
 */
export default function PromotionPopup() {
  const [promo, setPromo] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    axios.get(`${API}/promotions/active`).then((r) => {
      if (!active) return;
      const data = r.data;
      if (!data) return;
      const dismissed = (() => {
        try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || 'null'); } catch { return null; }
      })();
      const stillDismissed = dismissed && dismissed.id === data.id && dismissed.version === data.version;
      if (stillDismissed) return;
      setPromo(data);
      // Tiny delay to not fight the page's initial paint
      setTimeout(() => active && setOpen(true), 400);
    }).catch(() => { /* public endpoint failure → silently drop */ });
    return () => { active = false; };
  }, []);

  const dismiss = () => {
    setOpen(false);
    if (promo) {
      try {
        localStorage.setItem(DISMISS_KEY, JSON.stringify({ id: promo.id, version: promo.version }));
      } catch { /* quota, ignore */ }
    }
  };

  const handleClick = () => {
    if (!promo?.button_url) return;
    const url = String(promo.button_url).trim();
    dismiss();
    // Internal links keep the SPA session; external links open in a new tab.
    const isExternal = /^https?:\/\//i.test(url);
    if (isExternal) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url.startsWith('/') ? url : `/${url}`);
    }
  };

  if (!open || !promo) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={dismiss}
      data-testid="promotion-popup-overlay"
    >
      <div
        className="relative bg-[#0A0A0A] border border-[#B5FF00]/40 shadow-[0_0_60px_rgba(181,255,0,0.15)] max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="promotion-popup"
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 z-10 text-zinc-400 hover:text-white bg-black/60 p-1.5 rounded-full"
          data-testid="promotion-dismiss-btn"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {promo.image_url && (
          <div className="w-full aspect-[4/3] overflow-hidden bg-zinc-900">
            <img
              src={promo.image_url}
              alt=""
              className="w-full h-full object-cover"
              data-testid="promotion-popup-image"
            />
          </div>
        )}

        <div className="p-5 sm:p-6">
          {promo.text && (
            <p
              className="text-white text-base sm:text-lg font-['Josefin_Sans'] leading-snug whitespace-pre-wrap"
              data-testid="promotion-popup-text"
            >
              {promo.text}
            </p>
          )}

          {promo.button_label && promo.button_url && (
            <button
              type="button"
              onClick={handleClick}
              className="btn-primary w-full mt-5"
              data-testid="promotion-popup-button"
            >
              {promo.button_label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
